// src/components/PurchaseLink.tsx

import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { marketingConsentText, marketingConsentVersion } from '../config/marketing';

interface PurchaseLinkProps {
  priceId: string;
  productSlug: string;
}

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : Promise.resolve(null);

const PurchaseLink: React.FC<PurchaseLinkProps> = ({ priceId, productSlug }) => {
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [productAnnouncementsOptIn, setProductAnnouncementsOptIn] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handlePurchase = async () => {
    try {
      setIsRedirecting(true);
      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          productSlug,
          newsletterOptIn,
          productAnnouncementsOptIn,
          marketingConsentVersion
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create Checkout Session');
      }

      const data = await response.json();

      if (data.id) {
        const stripe = await stripePromise;
        if (stripe) {
          await stripe.redirectToCheckout({ sessionId: data.id });
        } else {
          console.error('Stripe.js failed to load.');
          alert('Stripe failed to load. Please try again later.');
        }
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Error:', error.message);
      alert(`Purchase failed: ${error.message}`);
      setIsRedirecting(false);
    }
  };

  return (
    <div className="mt-6">
      <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
        <input
          type="checkbox"
          checked={newsletterOptIn}
          onChange={(event) => setNewsletterOptIn(event.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
        />
        <span>{marketingConsentText.newsletter}</span>
      </label>
      <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer mt-3">
        <input
          type="checkbox"
          checked={productAnnouncementsOptIn}
          onChange={(event) => setProductAnnouncementsOptIn(event.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
        />
        <span>{marketingConsentText.productAnnouncements}</span>
      </label>
      <p className="mt-2 text-xs text-gray-500">Both are optional. You can unsubscribe at any time.</p>
      <button
        onClick={handlePurchase}
        disabled={isRedirecting}
        className="w-full bg-green-600 text-white py-3 px-6 rounded-lg text-lg font-semibold hover:bg-green-700 disabled:bg-green-400 disabled:cursor-wait transition-colors flex items-center justify-center mt-4"
      >
        <Download className="mr-2 h-5 w-5" />
        {isRedirecting ? 'Opening secure checkout…' : 'Purchase Now'}
      </button>
    </div>
  );
};

export default PurchaseLink;
